import React, { useState, useEffect } from 'react';
import { useProgramManagement } from '@/hooks/useProgramManagement';
import { useTeamData } from '@/hooks/useTeamData';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProgramDialog } from './ProgramDialog';
import { ProgramTable } from './ProgramTable';
import { ActivityManager } from './ActivityManager';
import { PracticeRunner } from './PracticeRunner';
import { ProgramPlayerAssignments } from './ProgramPlayerAssignments';
import { QuizManagement } from './QuizManagement';
import { Plus, BookOpen } from 'lucide-react';
import { Program } from '@/types/program';

interface ProgramManagementProps {
  clubId?: string;
  teamId?: string; // New prop to filter programs for a specific team
  isDialogOpen?: boolean;
  setIsDialogOpen?: (open: boolean) => void;
  editingProgram?: Program | null;
  setEditingProgram?: (program: Program | null) => void;
}

export const ProgramManagement = ({ 
  clubId, 
  teamId, 
  isDialogOpen: externalIsDialogOpen, 
  setIsDialogOpen: externalSetIsDialogOpen,
  editingProgram: externalEditingProgram,
  setEditingProgram: externalSetEditingProgram
}: ProgramManagementProps) => {
  const { teams } = useTeamData(clubId);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(teamId || '');
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [showActivityManager, setShowActivityManager] = useState(false);
  const [showPracticeRunner, setShowPracticeRunner] = useState(false);
  const [showPlayerAssignments, setShowPlayerAssignments] = useState(false);
  const [showQuizManager, setShowQuizManager] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);

  // Use external state if provided, otherwise use internal state
  const dialogOpen = externalIsDialogOpen !== undefined ? externalIsDialogOpen : isDialogOpen;
  const setDialogOpen = externalSetIsDialogOpen || setIsDialogOpen;
  const currentEditingProgram = externalEditingProgram !== undefined ? externalEditingProgram : editingProgram;
  const setCurrentEditingProgram = externalSetEditingProgram || setEditingProgram;

  const { programs, loading, createProgram, updateProgram, deleteProgram } = useProgramManagement(selectedTeamId);

  // Set the team ID when teamId prop is provided (from team-specific page)
  useEffect(() => {
    if (teamId) {
      setSelectedTeamId(teamId);
    }
  }, [teamId]);

  const handleCreateProgram = async (programData: Omit<Program, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      await createProgram(programData);
      setDialogOpen(false);
      setCurrentEditingProgram(null);
    } catch (error) {
      console.error('Failed to create program:', error);
    }
  };

  const handleEditProgram = (program: Program) => {
    setCurrentEditingProgram(program);
    setDialogOpen(true);
  };

  const handleUpdateProgram = async (programData: Omit<Program, 'id' | 'created_at' | 'updated_at'>) => {
    if (!currentEditingProgram) return;
    
    try {
      await updateProgram(currentEditingProgram.id, programData);
      setDialogOpen(false);
      setCurrentEditingProgram(null);
    } catch (error) {
      console.error('Failed to update program:', error);
    }
  };

  const handleDeleteProgram = async (programId: string) => {
    if (!confirm('Are you sure you want to delete this program? This will also delete all associated activities.')) {
      return;
    }

    try {
      await deleteProgram(programId);
    } catch (error) {
      console.error('Failed to delete program:', error);
    }
  };

  const handleManageActivities = (program: Program) => {
    setSelectedProgram(program);
    setShowActivityManager(true);
  };

  const handleRunPractice = (program: Program) => {
    setSelectedProgram(program);
    setShowPracticeRunner(true);
  };

  const handleManagePlayers = (program: Program) => {
    setSelectedProgram(program);
    setShowPlayerAssignments(true);
  };

  const handleManageQuizzes = (program: Program) => {
    setSelectedProgram(program);
    setShowQuizManager(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentEditingProgram(null);
  };

  if (showActivityManager && selectedProgram) {
    return (
      <ActivityManager
        program={selectedProgram}
        onBack={() => {
          setShowActivityManager(false);
          setSelectedProgram(null);
        }}
      />
    );
  }

  if (showPracticeRunner && selectedProgram) {
    return (
      <PracticeRunner
        program={selectedProgram}
        onBack={() => {
          setShowPracticeRunner(false);
          setSelectedProgram(null);
        }}
      />
    );
  }

  if (showPlayerAssignments && selectedProgram) {
    return (
      <ProgramPlayerAssignments
        programId={selectedProgram.id}
        teamId={selectedTeamId}
        programTitle={selectedProgram.title}
        onBack={() => {
          setShowPlayerAssignments(false);
          setSelectedProgram(null);
        }}
      />
    );
  }

  if (showQuizManager && selectedProgram) {
    return (
      <QuizManagement
        programId={selectedProgram.id}
        programTitle={selectedProgram.title}
        onBack={() => {
          setShowQuizManager(false);
          setSelectedProgram(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">

      {/* Team Selection - Hide if teamId is provided */}
      {!teamId && (
        <Card>
          <CardHeader>
            <CardTitle>Select Team</CardTitle>
            <CardDescription>
              Choose a team to view and manage its programs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a team..." />
              </SelectTrigger>
              <SelectContent>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Programs Table */}
      {selectedTeamId && (
          <ProgramTable
            programs={programs}
            loading={loading}
            onEdit={handleEditProgram}
            onDelete={handleDeleteProgram}
            onManageActivities={handleManageActivities}
            onRunPractice={handleRunPractice}
            onManagePlayers={handleManagePlayers}
            onManageQuizzes={handleManageQuizzes}
          />
      )}

      {/* Program Dialog */}
      <ProgramDialog
        isOpen={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={currentEditingProgram ? handleUpdateProgram : handleCreateProgram}
        program={currentEditingProgram}
        teamId={selectedTeamId}
      />
    </div>
  );
};
